import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Clock, Ban, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { getErrorMessage } from '@/lib/errors';

const ROLES_HORARIO = [ROL.SUPER_ADMIN, ROL.MEDICO, ROL.MEDICO_ADMIN, ROL.ADMIN_CLINICA];

const DIAS: { key: string; label: string }[] = [
  { key: 'lunes',     label: 'Lunes'    },
  { key: 'martes',    label: 'Martes'   },
  { key: 'miercoles', label: 'Miércoles'},
  { key: 'jueves',    label: 'Jueves'   },
  { key: 'viernes',   label: 'Viernes'  },
  { key: 'sabado',    label: 'Sábado'   },
  { key: 'domingo',   label: 'Domingo'  },
];

const EMPTY_FORM = {
  medicoId: '', clinicaId: '', diaSemana: 'lunes',
  horaInicio: '08:00', horaFin: '14:00', esBloqueo: false,
};

export function BloquesHorarioPage() {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();

  const canManage = !!(usuario?.rolId && (ROLES_HORARIO as string[]).includes(usuario.rolId));

  const [filterMedico, setFilterMedico] = useState('');
  const [filterClinica, setFilterClinica] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [formDay, setFormDay] = useState('lunes');
  const [form, setForm] = useState(EMPTY_FORM);

  const set = (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [field]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const { data: bloques = [] } = useQuery({
    queryKey: ['bloques-horario', filterMedico, filterClinica],
    queryFn: () =>
      api.get('/api/bloques-horario', {
        params: {
          ...(filterMedico  && { medicoId:  filterMedico }),
          ...(filterClinica && { clinicaId: filterClinica }),
        },
      }).then(r => r.data),
    enabled: canManage,
  });

  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos-select'],
    queryFn: () => api.get('/api/medicos').then(r => r.data),
    enabled: canManage,
  });

  const { data: clinicas = [] } = useQuery({
    queryKey: ['clinicas'],
    queryFn: () => api.get('/api/clinicas').then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/bloques-horario', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bloques-horario'] });
      toast.success('Bloque horario agregado');
      setFormOpen(false);
      setForm(EMPTY_FORM);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear el bloque')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/bloques-horario/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bloques-horario'] });
      toast.success('Bloque eliminado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar el bloque')),
  });

  function openFormForDay(dia: string) {
    setFormDay(dia);
    setForm({ ...EMPTY_FORM, diaSemana: dia });
    setFormOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate(form);
  }

  const bloquesByDay = DIAS.reduce<Record<string, any[]>>((acc, { key }) => {
    acc[key] = (bloques as any[]).filter((b: any) => b.diaSemana === key);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <p className="text-sm text-text-secondary">Disponibilidad semanal por médico</p>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filterMedico}
          onChange={e => setFilterMedico(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
        >
          <option value="">Todos los médicos</option>
          {(medicos as any[]).map((m: any) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
        </select>
        <select
          value={filterClinica}
          onChange={e => setFilterClinica(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
        >
          <option value="">Todas las clínicas</option>
          {(clinicas as any[]).map((c: any) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
      </div>

      {/* Weekly grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {DIAS.map(({ key, label }) => {
          const dayBloques = bloquesByDay[key] || [];
          return (
            <div key={key} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                <span className="text-sm font-semibold text-text-primary">{label}</span>
                {canManage && (
                  <button
                    onClick={() => openFormForDay(key)}
                    className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-primary-600 transition-colors"
                  >
                    <Plus size={15} />
                  </button>
                )}
              </div>

              <div className="p-3 space-y-2 min-h-[80px]">
                {dayBloques.length === 0 ? (
                  <p className="text-xs text-text-secondary text-center py-4">Sin bloques</p>
                ) : (
                  dayBloques.map((b: any) => (
                    <div
                      key={b.id}
                      className={cn(
                        'flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm',
                        b.esBloqueo
                          ? 'bg-red-50 border border-red-100'
                          : 'bg-primary-50 border border-primary-100',
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {b.esBloqueo ? (
                          <Ban size={12} className="text-red-400 flex-shrink-0" />
                        ) : (
                          <CheckCircle2 size={12} className="text-primary-500 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className={cn('text-xs font-medium truncate', b.esBloqueo ? 'text-red-700' : 'text-primary-700')}>
                            {b.horaInicio} – {b.horaFin}
                          </p>
                          {b.medico?.nombre && (
                            <p className="text-[10px] text-text-secondary truncate">{b.medico.nombre}</p>
                          )}
                        </div>
                      </div>
                      {canManage && (
                        <button
                          onClick={() => deleteMutation.mutate(b.id)}
                          disabled={deleteMutation.isPending}
                          className="p-0.5 rounded text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 disabled:opacity-40"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Form dialog */}
      <Dialog.Root open={formOpen} onOpenChange={setFormOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 focus:outline-none">
            <div className="flex items-center justify-between mb-5">
              <Dialog.Title className="text-base font-semibold text-text-primary">
                Nuevo bloque — {DIAS.find(d => d.key === formDay)?.label}
              </Dialog.Title>
              <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                <X size={16} />
              </Dialog.Close>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Médico <span className="text-red-500">*</span>
                </label>
                <select
                  required value={form.medicoId} onChange={set('medicoId')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                >
                  <option value="">Seleccionar médico</option>
                  {(medicos as any[]).map((m: any) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Clínica <span className="text-red-500">*</span>
                </label>
                <select
                  required value={form.clinicaId} onChange={set('clinicaId')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                >
                  <option value="">Seleccionar clínica</option>
                  {(clinicas as any[]).map((c: any) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Inicio</label>
                  <div className="relative">
                    <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      required type="time" value={form.horaInicio} onChange={set('horaInicio')}
                      className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Fin</label>
                  <div className="relative">
                    <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      required type="time" value={form.horaFin} onChange={set('horaFin')}
                      className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                    />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.esBloqueo}
                  onChange={set('esBloqueo')}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-300"
                />
                <div>
                  <p className="text-sm font-medium text-text-primary">Marcar como bloqueo</p>
                  <p className="text-xs text-text-secondary">El médico no estará disponible en este horario</p>
                </div>
              </label>

              <div className="flex gap-3 pt-2">
                <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                  Cancelar
                </Dialog.Close>
                <button
                  type="submit" disabled={createMutation.isPending}
                  className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60"
                >
                  {createMutation.isPending ? 'Guardando...' : 'Agregar bloque'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
